import { useEffect, useState } from 'react';
import { useTranslation } from '@/i18n';
import { SlideOver, SlideOverFooter } from '@/components/Shared/SlideOver';
import { FormField } from '@/components/Shared/FormField';
import { ChoiceTiles } from '@/components/Shared/ChoiceTiles';
import { formFieldInputClassName } from '@/components/Shared/formFieldStyles';
import { useEmployeeStore } from '@/store/useEmployeeStore';
import {
  FacilityFlowCoreModulesFleetEntitiesVehicleLogbookEntryKind as EntryKind,
  type FacilityFlowCoreModulesFleetEntitiesTripPurposeKind as PurposeKind,
} from '@/api/generated/models';
import {
  useVehicleLogbookStore,
  type UpdateVehicleLogbookEntryRequest,
  type VehicleLogbookEntryDto,
} from '@/store/useVehicleLogbookStore';
import { DateTimeFields, LogbookHistory } from '@/pages/VehicleLogbook/LogbookHistory';
import { DESTINATION_MAX_LENGTH, MAX_ODOMETER_KM, PURPOSE_MAX_LENGTH } from '@/utils/logbookLimits';
import { isoDate, localTimeToUtcIso, parseUtcTimestamp, utcToLocalTimeInputValue } from '@/utils/dateUtils';
import { BUSINESS, PRIVATE } from '@/utils/vehicleTrips';

interface LogbookEntryFormProps {
  entry: VehicleLogbookEntryDto | null;
  saving: boolean;
  onClose: () => void;
  onSave: (data: UpdateVehicleLogbookEntryRequest) => Promise<boolean>;
}

/** Empty → null; otherwise whole kilometres, or undefined when invalid. */
function parseKm(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const km = Number(trimmed);
  return km <= MAX_ODOMETER_KM ? km : undefined;
}

const toText = (km?: number | null) => (km != null ? String(km) : '');

/** Local day of a UTC timestamp as YYYY-MM-DD, or '' when there is none. */
const localDayOf = (timestamp?: string | null) => {
  const date = parseUtcTimestamp(timestamp);
  return date ? isoDate(date) : '';
};

const sameTime = (a?: string | null, b?: string | null) =>
  parseUtcTimestamp(a)?.getTime() === parseUtcTimestamp(b)?.getTime();

interface FormState {
  startKm: string;
  endKm: string;
  startLocation: string;
  destination: string;
  purpose: string;
  purposeKind: PurposeKind;
  startedBy: string;
  endedBy: string;
  departureDate: string;
  departureTime: string;
  arrivalDate: string;
  arrivalTime: string;
}

function stateOf(entry: VehicleLogbookEntryDto): FormState {
  return {
    startKm: toText(entry.startOdometerKm),
    endKm: toText(entry.endOdometerKm),
    startLocation: entry.startLocation ?? '',
    destination: entry.destination ?? '',
    purpose: entry.purpose ?? '',
    purposeKind: entry.purposeKind ?? BUSINESS,
    startedBy: entry.startedByEmployeeId ?? '',
    endedBy: entry.endedByEmployeeId ?? '',
    // A trip without start departs on the day it arrived, unless set otherwise.
    departureDate: localDayOf(entry.startedAt) || localDayOf(entry.endedAt),
    departureTime: utcToLocalTimeInputValue(entry.startedAt),
    arrivalDate: localDayOf(entry.endedAt),
    arrivalTime: utcToLocalTimeInputValue(entry.endedAt),
  };
}

/**
 * Correct one logbook entry (FAFD-26, FAFD-68): departure and arrival, readings, start location,
 * destination and purpose, the kind of trip where private trips are allowed, and — after a
 * wrong role — the drivers. The time clock no longer moves trips (ADR-0027); the logbook is
 * corrected here only. Every changed field is kept by the backend with name and time; saving
 * without a change sends nothing.
 */
export default function LogbookEntryForm({ entry, saving, onClose, onSave }: LogbookEntryFormProps) {
  const { t } = useTranslation(['vehicles', 'common']);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | undefined>();
  const showPurposeKind = useVehicleLogbookStore((s) => s.overview?.showPurposeKind ?? false);

  // All employees, also inactive ones — an old entry may refer to them.
  const employees = useEmployeeStore((s) => s.employees);
  const fetchAllEmployees = useEmployeeStore((s) => s.fetchAllEmployees);
  useEffect(() => {
    if (!entry) return;
    fetchAllEmployees();
    setForm(stateOf(entry));
    setError(undefined);
  }, [entry, fetchAllEmployees]);

  const isTrip = entry?.kind !== EntryKind.NUMBER_2;
  const f = form ?? (entry ? stateOf(entry) : null);
  const departureIso = isTrip && f?.departureDate && f.departureTime ? localTimeToUtcIso(f.departureDate, f.departureTime) : null;
  const arrivalIso = isTrip && entry?.endedAt && f?.arrivalDate && f.arrivalTime ? localTimeToUtcIso(f.arrivalDate, f.arrivalTime) : null;
  const departureChanged = departureIso !== null && !sameTime(departureIso, entry?.startedAt);
  const arrivalChanged = arrivalIso !== null && !sameTime(arrivalIso, entry?.endedAt);
  const touched = entry !== null && f !== null && JSON.stringify(f) !== JSON.stringify(stateOf(entry));

  const update = (change: Partial<FormState>) => {
    setForm((prev) => (prev ? { ...prev, ...change } : prev));
    setError(undefined);
  };

  const handleSubmit = async () => {
    if (!entry || !f) return;
    // why: an unchanged save must neither write a revision nor report "aktualisiert".
    if (!touched) {
      onClose();
      return;
    }
    const start = parseKm(f.startKm);
    const end = parseKm(f.endKm);
    if (start === undefined || end === undefined || (!isTrip && start === null)) {
      setError(t('vehicles:validation.odometerInvalid'));
      return;
    }
    if (departureChanged && !f.startedBy) {
      setError(t('vehicles:validation.departureNeedsDriver'));
      return;
    }
    const departure = parseUtcTimestamp(departureIso ?? entry.startedAt);
    const arrival = parseUtcTimestamp(arrivalIso ?? entry.endedAt);
    if (departure && arrival && departure.getTime() > arrival.getTime()) {
      setError(t('vehicles:validation.departureAfterArrival'));
      return;
    }
    const saved = await onSave({
      // Only changed times, drivers and kind are sent — null keeps them.
      startedAt: departureChanged ? departureIso : null,
      endedAt: arrivalChanged ? arrivalIso : null,
      startOdometerKm: start,
      endOdometerKm: isTrip ? end : null,
      startLocation: isTrip ? f.startLocation.trim() || null : null,
      destination: isTrip ? f.destination.trim() || null : null,
      purpose: isTrip ? f.purpose.trim() || null : null,
      ...(isTrip && f.purposeKind !== (entry.purposeKind ?? BUSINESS) ? { purposeKind: f.purposeKind } : {}),
      startedByEmployeeId: f.startedBy && f.startedBy !== entry.startedByEmployeeId ? f.startedBy : null,
      endedByEmployeeId: f.endedBy && f.endedBy !== entry.endedByEmployeeId ? f.endedBy : null,
    });
    if (saved) onClose();
  };

  /** currentName keeps a driver selectable who is no longer in the employee list. */
  const employeeSelect = (label: string, key: 'startedBy' | 'endedBy', currentName?: string | null) => {
    const value = f?.[key] ?? '';
    return (
      <FormField label={label}>
        <select value={value} onChange={(e) => update({ [key]: e.target.value })} className={formFieldInputClassName(false)}>
          {!value && <option value="">—</option>}
          {value && !employees.some((e) => e.id === value) && <option value={value}>{currentName ?? '—'}</option>}
          {[...employees]
            .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'de'))
            .map((e) => (
              <option key={e.id} value={e.id}>
                {`${e.firstName ?? ''} ${e.lastName ?? ''}`.trim()}
                {e.personalNumber ? ` (${e.personalNumber})` : ''}
              </option>
            ))}
        </select>
      </FormField>
    );
  };

  const kmField = (label: string, key: 'startKm' | 'endKm', name: string) => (
    <FormField
      label={label}
      name={name}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={f?.[key] ?? ''}
      onChange={(e) => update({ [key]: e.target.value.replace(/\D/g, '') })}
      className="font-mono tabular-nums"
    />
  );

  const textField = (label: string, key: 'startLocation' | 'destination' | 'purpose', maxLength: number) => (
    <FormField label={label} name={key} value={f?.[key] ?? ''} maxLength={maxLength} onChange={(e) => update({ [key]: e.target.value })} />
  );

  return (
    <SlideOver
      isOpen={entry !== null}
      onClose={onClose}
      title={t('vehicles:logbook.edit.title')}
      size="md"
      hasUnsavedChanges={touched && !saving}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          confirmText={t('common:actions.save')}
          cancelText={t('common:actions.cancel')}
          loading={saving}
        />
      }
    >
      <div className="space-y-5">
        <p className="text-[12px] text-slate-500">{t('vehicles:logbook.edit.hint')}</p>

        {isTrip && f && (
          <div className="space-y-3">
            <DateTimeFields
              dateLabel={t('vehicles:logbook.edit.departureDate')}
              timeLabel={t('vehicles:logbook.edit.departureTime')}
              date={f.departureDate}
              time={f.departureTime}
              name="departure"
              onChange={(c) => update({ departureDate: c.date ?? f.departureDate, departureTime: c.time ?? f.departureTime })}
            />
            {entry?.endedAt && (
              <DateTimeFields
                dateLabel={t('vehicles:logbook.edit.arrivalDate')}
                timeLabel={t('vehicles:logbook.edit.arrivalTime')}
                date={f.arrivalDate}
                time={f.arrivalTime}
                name="arrival"
                onChange={(c) => update({ arrivalDate: c.date ?? f.arrivalDate, arrivalTime: c.time ?? f.arrivalTime })}
              />
            )}
          </div>
        )}

        {isTrip && (entry?.startedAt || entry?.endedAt || departureIso) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(entry?.startedAt || departureIso) &&
              employeeSelect(t('vehicles:logbook.edit.driverDeparture'), 'startedBy', entry?.startedByName)}
            {entry?.endedAt && employeeSelect(t('vehicles:logbook.edit.driverArrival'), 'endedBy', entry?.endedByName)}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {kmField(isTrip ? t('vehicles:logbook.columns.startKm') : t('vehicles:logbook.columns.reading'), 'startKm', 'startOdometerKm')}
          {isTrip && kmField(t('vehicles:logbook.columns.endKm'), 'endKm', 'endOdometerKm')}
        </div>
        {error && <p className="text-[11px] text-red-600">{error}</p>}

        {isTrip && f && (
          <>
            {showPurposeKind && (
              <ChoiceTiles
                legend={t('vehicles:trip.kindLabel')}
                compact
                value={f.purposeKind === PRIVATE ? 'private' : 'business'}
                onChange={(kind) => update({ purposeKind: kind === 'private' ? PRIVATE : BUSINESS })}
                options={[
                  { value: 'business', label: t('vehicles:trip.kind.business') },
                  { value: 'private', label: t('vehicles:trip.kind.private') },
                ]}
              />
            )}
            {textField(t('vehicles:logbook.columns.startLocation'), 'startLocation', DESTINATION_MAX_LENGTH)}
            {textField(t('vehicles:logbook.columns.destination'), 'destination', DESTINATION_MAX_LENGTH)}
            {textField(t('vehicles:logbook.columns.purpose'), 'purpose', PURPOSE_MAX_LENGTH)}
          </>
        )}

        <LogbookHistory revisions={entry?.revisions ?? []} />
      </div>
    </SlideOver>
  );
}
